/* Portal data store — loads the signed-in customer's access-scoped collection
   once, and owns the portfolios list (CRUD persisted to the VPS, mirrored into
   local state so the UI stays responsive). */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import * as api from '@/portal/api/client';
import type { Portfolio, PortfolioDisplay } from '@/portal/api/types';
import { toProduct } from '@/portal/data/product';
import type { PortalProduct } from '@/portal/api/types';

interface PortalData {
  collection: PortalProduct[];
  productByCode: Map<string, PortalProduct>;
  collectionLoading: boolean;
  collectionError: string | null;
  portfolios: Portfolio[];
  portfoliosLoading: boolean;
  reloadPortfolios: () => Promise<void>;
  createPortfolio: (payload?: { title?: string; client?: string; cover_code?: string }) => Promise<Portfolio>;
  patchPortfolio: (
    id: string,
    patch: Partial<{ title: string; client: string; event: string; note: string; cover_code: string; status: string; display: PortfolioDisplay }>,
  ) => Promise<Portfolio>;
  setPortfolioItems: (id: string, codes: string[]) => Promise<Portfolio>;
  sharePortfolio: (id: string) => Promise<Portfolio>;
  removePortfolio: (id: string) => Promise<void>;
}

const Ctx = createContext<PortalData | null>(null);

export function PortalDataProvider({ children }: { children: ReactNode }) {
  const [collection, setCollection] = useState<PortalProduct[]>([]);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [portfoliosLoading, setPortfoliosLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const items = await api.fetchItems();
        if (!alive) return;
        const products = items.map(toProduct).filter((p) => p.status === 'live');
        setCollection(products);
      } catch (e) {
        if (alive) setCollectionError(e instanceof Error ? e.message : 'Failed to load collection');
      } finally {
        if (alive) setCollectionLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const reloadPortfolios = useCallback(async () => {
    setPortfoliosLoading(true);
    try {
      const list = await api.listPortfolios();
      setPortfolios(list);
    } finally {
      setPortfoliosLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadPortfolios();
  }, [reloadPortfolios]);

  const upsert = useCallback((pf: Portfolio) => {
    setPortfolios((prev) => {
      const idx = prev.findIndex((p) => p.id === pf.id);
      if (idx === -1) return [pf, ...prev];
      const next = [...prev];
      next[idx] = pf;
      return next;
    });
  }, []);

  const createPortfolio = useCallback(
    async (payload?: { title?: string; client?: string; cover_code?: string }) => {
      const pf = await api.createPortfolio(payload ?? {});
      upsert(pf);
      return pf;
    },
    [upsert],
  );

  const patchPortfolio = useCallback<PortalData['patchPortfolio']>(
    async (id, patch) => {
      const pf = await api.updatePortfolio(id, patch);
      upsert(pf);
      return pf;
    },
    [upsert],
  );

  const setPortfolioItems = useCallback<PortalData['setPortfolioItems']>(
    async (id, codes) => {
      const pf = await api.replacePortfolioItems(id, codes);
      upsert(pf);
      return pf;
    },
    [upsert],
  );

  const sharePortfolio = useCallback<PortalData['sharePortfolio']>(
    async (id) => {
      const res = await api.sharePortfolio(id);
      const updated = await api.getPortfolio(id);
      upsert(updated);
      void res;
      return updated;
    },
    [upsert],
  );

  const removePortfolio = useCallback(async (id: string) => {
    await api.deletePortfolio(id);
    setPortfolios((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const productByCode = useMemo(() => {
    const m = new Map<string, PortalProduct>();
    collection.forEach((p) => m.set(p.code, p));
    return m;
  }, [collection]);

  const value = useMemo<PortalData>(
    () => ({
      collection,
      productByCode,
      collectionLoading,
      collectionError,
      portfolios,
      portfoliosLoading,
      reloadPortfolios,
      createPortfolio,
      patchPortfolio,
      setPortfolioItems,
      sharePortfolio,
      removePortfolio,
    }),
    [
      collection,
      productByCode,
      collectionLoading,
      collectionError,
      portfolios,
      portfoliosLoading,
      reloadPortfolios,
      createPortfolio,
      patchPortfolio,
      setPortfolioItems,
      sharePortfolio,
      removePortfolio,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePortalData(): PortalData {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePortalData must be used within PortalDataProvider');
  return ctx;
}
